import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { BaseControlComponent } from './base-control.component';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';

@Component({
    selector: 'app-select-control',
    template: `
    <div class="app-pr1 app-pb1" [ngClass]="className">
      <mat-form-field [formGroup]="formGroup">
        <mat-label>{{ label | translation: translationRequest }}</mat-label>
    
        <mat-select
          #select
          [formControlName]="formControlName"
          autocomplete="off"
          [placeholder]="placeHolder"
          [multiple]="multiple"
          (selectionChange)="selectionChange($event)"
          (openedChange)="openedChange($event)"
          aria-haspopup="listbox"
          aria-controls="optionsList"
          aria-autocomplete="none"
          >
          @if (!multiple) {
            <mat-option class="contains-mat-select-search">
              <ngx-mat-select-search
                (keyup)="onKey($event)"
                [placeholderLabel]="'find option...' | translation: translationRequest | sentenceCase"
                [noEntriesFoundLabel]="'Option not found' | translation: translationRequest"
              ></ngx-mat-select-search>
            </mat-option>
          }
          <div id="optionsList" role="listbox">
            @if (multiple) {
              <mat-option
                (click)="toggleAllSelection()"
                (keydown.enter)="toggleAllSelection()"
                [value]="selectAllValue"
                [class.select-all]="false"
                >
                <span>{{ 'Select All' | translation: translationRequest }}</span>
              </mat-option>
            }
    
            @for (option of options; track option) {
              <mat-option [value]="option.value">{{
                translationNeeded ? (option.label | translation: translationRequest) : option.label
              }}</mat-option>
            }
          </div>
        </mat-select>
        @if (formControl.invalid) {
          <mat-error>{{ errorMessage | translation: translationRequest }}</mat-error>
        }
      </mat-form-field>
    </div>
    `,
    styles: [``],
    standalone: false
})
export class SelectControlComponent
  extends BaseControlComponent
  implements OnInit, OnDestroy {
  private _select: MatSelect;

  @ViewChild('select')
  set select(ref: MatSelect) {
    this._select = ref;
  }

  get select(): MatSelect {
    return this._select;
  }

  allChecked: boolean = false;

  allIndeterminate: boolean = false;

  @Input()
  options: SelectOption[] = [];

  filteredOptions: SelectOption[] = [];

  optionsAll: SelectOption[] = [];

  @Input()
  multiple: boolean = false;

  @Input()
  className: string = '';

  @Input()
  translationNeeded: boolean = true;

  translationRequest = new TranslationRequest({
        language: sessionStorage.getItem('languagepreference'),
        key: null,
        contNum: 0,
        tableName: null,
        primaryKey: null,
        primaryValue: null,
        lob: null,
        group: 'Shared',
        subGroup: 'Shared',
      });

  selectAllValue = 'selectAll';
  private _onDestroy = new Subject<void>();

  myControl = new FormControl();
  searchControl = new FormControl();

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.optionsAll = this.options;
  }

  onKey(event: any) {
    this.options = this.search(event.target.value);
  }

  search(value: string) {
    const filter = value.toLowerCase();

    this.filteredOptions = this.options.filter(
      (option) =>
        option.label.toLowerCase().startsWith(filter) ||
        option.value.toLowerCase().startsWith(filter)
    );

    if (filter.length == 0) {
      return this.optionsAll;
    } else {
      return this.filteredOptions;
    }
  }

  toggleAllSelection() {
    this.allChecked = !this.allChecked;
    this.allIndeterminate = false;
    if (this.allChecked) {
      this.select.options.forEach((item: MatOption) => item.select());
    } else {
      this.select.options.forEach((item: MatOption) => item.deselect());
    }
    this.updatePseudoCheckboxState();
  }

  selectionChange(selectChange: MatSelectChange) {
    const selectedValues = selectChange.value;
    this.allChecked = selectedValues.length === this.options.length;
    this.allIndeterminate = selectedValues.length > 0 && !this.allChecked;

    if (!this.allChecked) {
      this.select.options.forEach((item: MatOption) => {
        if (item.value === 'selectAll') {
          item.deselect();
        }
      });
    }
    this.updatePseudoCheckboxState();
  }

  openedChange(opened: boolean) {
    if (opened && this.select) {
      this.updateSelectAllState();
      // this.attachSpaceKeyListener();
      this.updatePseudoCheckboxState();
      if (this.select.panel) {
        this.select.panel.nativeElement.addEventListener('mouseleave', () => {
          this.select.close();
        });
      }
    }
  }

  updateSelectAllState() {
    const selectedValues = this.select.value || [];
    this.allChecked = selectedValues.length === this.options.length;
    this.allIndeterminate = selectedValues.length > 0 && !this.allChecked;
  }

  updatePseudoCheckboxState() {
    const selectAllOption = this.select.options.find(
      (option) => option.value === 'selectAll'
    );
    if (selectAllOption) {
      const pseudoCheckbox = selectAllOption
        ._getHostElement()
        .querySelector('.mat-pseudo-checkbox');
      pseudoCheckbox?.setAttribute('aria-hidden', 'false');
      if (pseudoCheckbox) {
        pseudoCheckbox.classList.toggle(
          'mat-pseudo-checkbox-checked',
          this.allChecked
        );
        pseudoCheckbox.classList.toggle(
          'mat-pseudo-checkbox-indeterminate',
          this.allIndeterminate
        );
        pseudoCheckbox.classList.toggle(
          'mat-pseudo-checkbox-unchecked',
          !this.allChecked && !this.allIndeterminate
        );
      }
    }
  }
}

export class SelectOption {
  label: string;
  value: any;
}
